import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import {
  Coins, PlayCircle, Eye, History as HistoryIcon, RefreshCw,
  CheckCircle2, AlertCircle, Mail, MailX, Clock,
  Search, X, ChevronUp, ChevronDown, ArrowUpDown,
  FileDown, Power, PowerOff,
} from "lucide-react";

// ─────────────────────────────────────────────────────────────────
// Maxlive88 · Admin → Reports → Monthly Commission Run
// Lets admin dry-run, execute, individually re-run, and audit the
// monthly 3% commission auto-credit job for any month/percentage.
// ─────────────────────────────────────────────────────────────────

const fmtBdt = (n) =>
  `৳ ${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const fmtDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

// Default = previous calendar month (e.g. on 1 June → "2026-05")
const defaultMonthYYYYMM = () => {
  const d = new Date();
  d.setDate(0); // last day of previous month
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}`;
};

const prettyMonth = (yyyymm) => {
  if (!yyyymm || !yyyymm.includes("-")) return yyyymm || "";
  const [y, m] = yyyymm.split("-").map(Number);
  if (!y || !m) return yyyymm;
  return new Date(y, m - 1, 1).toLocaleString("en-US", { month: "long", year: "numeric" });
};

export default function MonthlyCommissionRun() {
  const [month, setMonth] = useState(defaultMonthYYYYMM());
  const [pct, setPct] = useState("3");
  const [preview, setPreview] = useState(null); // { previews:[], total_commission_to_credit, ... }
  const [previewLoading, setPreviewLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null); // returned by real run
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [perAgentBusy, setPerAgentBusy] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;
  // Search · Filter · Sort
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all"); // all | ready | already | no_volume
  const [sortBy, setSortBy] = useState(""); // "" | username | deposit | withdraw | turnover | commission
  const [sortDir, setSortDir] = useState("desc"); // asc | desc

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const { data } = await api.get("/reports/agents/commission-bulk/history?limit=12");
      setHistory(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load history");
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runDry = async () => {
    setPreviewLoading(true);
    setPreview(null);
    setRunResult(null);
    try {
      const { data } = await api.post("/reports/agents/commission-bulk", {
        month,
        commission_pct: Number(pct) || 3,
        dry_run: true,
      });
      setPreview(data);
      toast.success(`Dry-run ready · ${data.agents_total} agents · ${fmtBdt(data.total_commission_to_credit)} would be credited`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Dry-run failed");
    } finally {
      setPreviewLoading(false);
    }
  };

  const runReal = async () => {
    const tot = preview?.total_commission_to_credit || 0;
    const eligible = (preview?.previews || []).filter((p) => !p.would_skip).length;
    if (!window.confirm(
      `Credit ${fmtBdt(tot)} commission to ${eligible} agent(s) for ${prettyMonth(month)} at ${pct}%?\n\n` +
      `Each agent's wallet will be credited AND an ultra-pro Commission email\nwill be sent from agent@maxlive88.com.\n\nThis is irreversible (per audit trail).`
    )) return;
    setRunning(true);
    setRunResult(null);
    try {
      const { data } = await api.post("/reports/agents/commission-bulk", {
        month,
        commission_pct: Number(pct) || 3,
      });
      setRunResult(data);
      toast.success(`Run complete · ${data.credited} credited · ${data.email_sent} emails · ${data.skipped_already_run} already · ${data.skipped_no_volume} no-volume`, { duration: 6000 });
      loadHistory();
      // Refresh preview so each row reflects the new "already_credited" state
      runDry();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Run failed");
    } finally {
      setRunning(false);
    }
  };

  const runForAgent = async (agentId, username) => {
    if (!window.confirm(`Force re-run for agent ${username} for ${prettyMonth(month)}?\n\nThis BYPASSES the idempotency lock (will credit + email even if previously credited).`)) return;
    setPerAgentBusy((m) => ({ ...m, [agentId]: true }));
    try {
      const { data } = await api.post("/reports/agents/commission-bulk", {
        month,
        commission_pct: Number(pct) || 3,
        only_agent_id: agentId,
      });
      toast.success(`${username}: credited=${data.credited} email_sent=${data.email_sent}`, { duration: 5000 });
      loadHistory();
      runDry();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally {
      setPerAgentBusy((m) => ({ ...m, [agentId]: false }));
    }
  };

  const eligibleCount = useMemo(() => (preview?.previews || []).filter((p) => !p.would_skip).length, [preview]);

  // Pagination — 12 agents per page, applied AFTER search/filter/sort
  const rawPreviews = preview?.previews || [];

  // 1) Filter by search term (username + email)
  const searchFiltered = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return rawPreviews;
    return rawPreviews.filter((p) => {
      return (
        String(p.agent_username || "").toLowerCase().includes(q) ||
        String(p.agent_email || "").toLowerCase().includes(q)
      );
    });
  }, [rawPreviews, searchTerm]);

  // 2) Filter by status
  const statusFiltered = useMemo(() => {
    if (statusFilter === "all") return searchFiltered;
    if (statusFilter === "ready") return searchFiltered.filter((p) => !p.would_skip);
    if (statusFilter === "already") return searchFiltered.filter((p) => p.would_skip === "already_credited");
    if (statusFilter === "no_volume") return searchFiltered.filter((p) => p.would_skip === "no_volume");
    return searchFiltered;
  }, [searchFiltered, statusFilter]);

  // 3) Sort
  const allPreviews = useMemo(() => {
    if (!sortBy) return statusFiltered;
    const arr = [...statusFiltered];
    const key = {
      username: "agent_username",
      deposit: "deposit_total",
      withdraw: "withdraw_total",
      turnover: "turnover",
      commission: "commission",
    }[sortBy];
    arr.sort((a, b) => {
      const av = a[key];
      const bv = b[key];
      if (sortBy === "username") {
        return String(av || "").localeCompare(String(bv || "")) * (sortDir === "asc" ? 1 : -1);
      }
      return ((Number(av) || 0) - (Number(bv) || 0)) * (sortDir === "asc" ? 1 : -1);
    });
    return arr;
  }, [statusFiltered, sortBy, sortDir]);

  const totalPages = Math.max(1, Math.ceil(allPreviews.length / PAGE_SIZE));
  // Clamp current page if data shrinks (e.g. after a re-run or filter)
  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);
  // Reset to page 1 whenever a brand-new preview lands OR filters/search/sort change
  useEffect(() => { setCurrentPage(1); }, [preview?.previews?.length, month, searchTerm, statusFilter, sortBy, sortDir]);

  const pageStart = (currentPage - 1) * PAGE_SIZE;
  const pageEnd = pageStart + PAGE_SIZE;
  const pagePreviews = allPreviews.slice(pageStart, pageEnd);

  const toggleSort = (key) => {
    if (sortBy !== key) {
      setSortBy(key);
      setSortDir("desc");
    } else if (sortDir === "desc") {
      setSortDir("asc");
    } else {
      // 3rd click clears sort
      setSortBy("");
      setSortDir("desc");
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setStatusFilter("all");
    setSortBy("");
    setSortDir("desc");
  };
  const filtersActive = searchTerm.trim().length > 0 || statusFilter !== "all" || !!sortBy;

  const [exportingPdf, setExportingPdf] = useState(false);
  // Auto-credit on/off + default % config
  const [config, setConfig] = useState(null); // { enabled, commission_pct, updated_at, updated_by }
  const [configBusy, setConfigBusy] = useState(false);

  const loadConfig = async () => {
    try {
      const { data } = await api.get("/reports/agents/commission-bulk/config");
      setConfig(data);
      // Sync the percent input with the persisted default so admin doesn't
      // accidentally run with a stale value.
      if (data && typeof data.commission_pct === "number") {
        setPct(String(data.commission_pct));
      }
    } catch (e) {
      // Non-fatal — page still works with hardcoded defaults
      // eslint-disable-next-line no-console
      console.warn("Failed to load auto-credit config", e);
    }
  };
  useEffect(() => { loadConfig(); }, []);

  const toggleAutoCredit = async () => {
    if (!config) return;
    const next = !config.enabled;
    if (!next) {
      if (!window.confirm(
        "Turn OFF auto-credit?\n\nThe monthly cron will SKIP its next run on the 1st @ 00:15 BD — no agent will be credited automatically. " +
        "Manual runs (this page or admin endpoints) will still work.\n\nProceed?"
      )) return;
    }
    setConfigBusy(true);
    try {
      const { data } = await api.put("/reports/agents/commission-bulk/config", { enabled: next });
      setConfig(data);
      toast.success(`Auto-credit is now ${next ? "ENABLED" : "DISABLED"}`, { duration: 5000 });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update");
    } finally {
      setConfigBusy(false);
    }
  };

  const saveDefaultPct = async () => {
    if (!config) return;
    const newPct = Number(pct);
    if (!(newPct >= 0 && newPct <= 100)) {
      toast.error("Commission rate must be between 0 and 100");
      return;
    }
    if (Math.abs(newPct - (config.commission_pct || 0)) < 1e-9) {
      toast.info("Already saved");
      return;
    }
    setConfigBusy(true);
    try {
      const { data } = await api.put("/reports/agents/commission-bulk/config", { commission_pct: newPct });
      setConfig(data);
      toast.success(`Default commission rate saved · ${data.commission_pct}%`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setConfigBusy(false);
    }
  };
  const exportPdf = async () => {
    if (allPreviews.length === 0) {
      toast.error("Nothing to export — table is empty.");
      return;
    }
    setExportingPdf(true);
    try {
      const payload = {
        month,
        commission_pct: Number(pct) || 3,
        filter_status: statusFilter,
        search_term: searchTerm.trim(),
        sort_by: sortBy || "",
        sort_dir: sortDir,
        rows: allPreviews.map((p) => ({
          agent_username: p.agent_username || "",
          agent_email: p.agent_email || "",
          deposit_total: Number(p.deposit_total) || 0,
          withdraw_total: Number(p.withdraw_total) || 0,
          turnover: Number(p.turnover) || 0,
          commission: Number(p.commission) || 0,
          would_skip: p.would_skip || null,
        })),
      };
      const res = await api.post(
        "/reports/agents/commission-bulk/export-pdf",
        payload,
        { responseType: "blob" }
      );
      // Build filename from Content-Disposition or fallback
      const cd = res.headers?.["content-disposition"] || "";
      const m = /filename="?([^"]+)"?/.exec(cd);
      const filename = (m && m[1]) || `maxlive88_commission_preview_${month}.pdf`;
      const blob = new Blob([res.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Downloaded · ${filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "PDF export failed");
    } finally {
      setExportingPdf(false);
    }
  };

  return (
    <div data-testid="monthly-commission-run-page">
      <PageHeader
        title="Monthly Commission Run"
        subtitle="Bulk-credit 3% (or custom %) commission to every active agent based on the prior month's NET successful volume (Approved Deposits − Approved Withdrawals) — then auto-email each via Agent Email Gateway."
      />

      {/* ── Schedule + Auto-Credit Toggle banner ─────────────────── */}
      <div
        className={`mb-5 rounded-lg border p-4 flex items-start gap-3 ${
          config?.enabled === false
            ? "border-rose-200 bg-gradient-to-r from-rose-50 via-amber-50 to-white"
            : "border-emerald-200 bg-gradient-to-r from-emerald-50 via-amber-50 to-white"
        }`}
        data-testid="commission-schedule-banner"
      >
        <div className={`h-11 w-11 rounded-md grid place-items-center flex-shrink-0 ${
          config?.enabled === false ? "bg-rose-100 text-rose-700" : "bg-emerald-100 text-emerald-700"
        }`}>
          {config?.enabled === false ? <PowerOff size={20} /> : <Power size={20} />}
        </div>
        <div className="flex-1 text-sm leading-relaxed min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`font-bold ${config?.enabled === false ? "text-rose-900" : "text-emerald-900"}`}>
              {config?.enabled === false ? "Auto-credit is OFF" : "Auto-credit is ON"}
            </span>
            <span className="text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-sm border bg-white/60 text-gray-600 border-gray-200">
              Default rate · {config?.commission_pct != null ? `${Number(config.commission_pct).toFixed(2)}%` : "3.00%"}
            </span>
            <span className="text-[10px] uppercase tracking-wider text-gray-500">
              Schedule · 1st of every month at 00:15 Asia/Dhaka
            </span>
          </div>
          <div className="text-gray-700 mt-1">
            {config?.enabled === false ? (
              <>
                <b className="text-rose-700">Cron will skip the next run.</b> Manual runs from this
                page (Dry-Run + Credit) still work. Toggle back ON to resume monthly automation.
              </>
            ) : (
              <>
                Cron will <b className="text-emerald-700">automatically credit {config?.commission_pct != null ? `${Number(config.commission_pct).toFixed(2)}%` : "3%"}</b> commission to every active agent based on their prior-month NET successful volume (Approved Deposits − Approved Withdrawals) — then email each via Agent Gateway.
              </>
            )}
            {config?.updated_at && (
              <span className="text-[11px] text-gray-500 ml-2">
                · Last changed by <b>{config.updated_by || "—"}</b> on {fmtDate(config.updated_at)}
              </span>
            )}
          </div>
        </div>
        <button
          onClick={toggleAutoCredit}
          disabled={!config || configBusy}
          className={`flex-shrink-0 inline-flex items-center gap-2 px-4 h-10 rounded-sm font-semibold text-sm border-2 transition ${
            config?.enabled === false
              ? "border-emerald-500 bg-emerald-500 text-white hover:bg-emerald-600 shadow"
              : "border-rose-300 bg-white text-rose-700 hover:bg-rose-50"
          } disabled:opacity-50`}
          data-testid="commission-toggle-auto-credit"
          title={config?.enabled === false ? "Enable monthly auto-credit cron" : "Disable monthly auto-credit cron"}
        >
          {configBusy
            ? "Saving…"
            : config?.enabled === false
              ? <><Power size={14} /> Enable Auto-Credit</>
              : <><PowerOff size={14} /> Disable Auto-Credit</>
          }
        </button>
      </div>

      {/* ── Controls card ─────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6" data-testid="commission-controls-card">
        <div className="px-5 py-4 bg-gradient-to-r from-emerald-900 to-emerald-700 text-white">
          <div className="flex items-center gap-2">
            <Coins size={18} className="text-amber-300" />
            <h3 className="font-bold text-base">Run Configuration</h3>
          </div>
        </div>
        <div className="p-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Settlement Period
              </label>
              <input
                type="month"
                value={month}
                onChange={(e) => setMonth(e.target.value)}
                className="w-full h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                data-testid="commission-run-month"
              />
              <div className="text-[11px] text-gray-500 mt-1">{prettyMonth(month) || "—"}</div>
            </div>
            <div>
              <label className="block text-[11px] uppercase tracking-wider text-gray-500 font-semibold mb-1">
                Commission Rate (%)
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  step="0.1"
                  min="0"
                  max="100"
                  value={pct}
                  onChange={(e) => setPct(e.target.value)}
                  className="flex-1 h-10 px-3 border border-gray-200 rounded-sm text-sm tabular"
                  data-testid="commission-run-pct"
                />
                <button
                  onClick={saveDefaultPct}
                  disabled={!config || configBusy || Number(pct) === Number(config?.commission_pct || 3)}
                  className="h-10 px-3 border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 rounded-sm text-xs font-semibold whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                  data-testid="commission-save-default-pct"
                  title="Persist this rate as the default — cron will use it next month"
                >
                  Save as default
                </button>
              </div>
              <div className="text-[11px] text-gray-500 mt-1">
                {config?.commission_pct != null
                  ? <>Default <b>{Number(config.commission_pct).toFixed(2)}%</b> · </>
                  : ""}
                Bonus months can be 4-5%.
              </div>
            </div>
            <div className="flex items-end gap-2">
              <button
                onClick={runDry}
                disabled={previewLoading || running}
                className="flex-1 h-10 px-3 rounded-sm text-sm font-semibold border border-emerald-300 text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 inline-flex items-center justify-center gap-2"
                data-testid="commission-run-dry"
              >
                <Eye size={14} /> {previewLoading ? "Computing…" : "Dry-Run Preview"}
              </button>
              <button
                onClick={runReal}
                disabled={!preview || running || eligibleCount === 0}
                className="flex-1 h-10 px-3 rounded-sm text-sm font-semibold text-white inline-flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: "linear-gradient(135deg,#0f5132 0%,#04210f 100%)", boxShadow: "0 2px 6px rgba(15,81,50,0.35)" }}
                data-testid="commission-run-execute"
              >
                <PlayCircle size={14} /> {running ? "Crediting…" : `Credit ${eligibleCount} agent${eligibleCount === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>

          {/* Summary tiles for the dry-run / completed run */}
          {(preview || runResult) && (
            <div className="mt-5 grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="commission-summary-tiles">
              <SummaryTile label="Total Agents" value={(runResult?.agents_total ?? preview?.agents_total) ?? "—"} testId="tile-total" />
              <SummaryTile label={runResult ? "Credited" : "Eligible"} value={runResult ? (runResult.credited ?? 0) : eligibleCount} accent="emerald" testId="tile-credited" />
              <SummaryTile label={runResult ? "Emails Sent" : "Total Commission"} value={runResult ? (runResult.email_sent ?? 0) : fmtBdt(preview?.total_commission_to_credit || 0)} accent="amber" testId="tile-amount" />
              <SummaryTile label="Already Credited" value={runResult ? (runResult.skipped_already_run ?? 0) : (preview?.previews || []).filter((p) => p.would_skip === "already_credited").length} accent="gray" testId="tile-already" />
              <SummaryTile label="No Volume" value={runResult ? (runResult.skipped_no_volume ?? 0) : (preview?.previews || []).filter((p) => p.would_skip === "no_volume").length} accent="gray" testId="tile-no-vol" />
            </div>
          )}
        </div>
      </div>

      {/* ── Preview table ─────────────────────────────────────────── */}
      {preview && (
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden mb-6" data-testid="commission-preview-card">
          <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-emerald-700 font-bold">Preview · {prettyMonth(month)}</div>
              <div className="text-sm text-gray-600">Per-agent breakdown — no money has been moved yet.</div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportPdf}
                disabled={exportingPdf || allPreviews.length === 0}
                className="text-xs font-semibold inline-flex items-center gap-1.5 px-3 h-8 rounded-sm border border-amber-300 text-amber-700 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="commission-export-pdf"
                title="Download the current filtered/sorted preview as a printable PDF"
              >
                <FileDown size={13} /> {exportingPdf ? "Building PDF…" : "Export PDF"}
              </button>
              <button onClick={runDry} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="commission-preview-refresh">
                <RefreshCw size={12} /> Refresh
              </button>
            </div>
          </div>

          {/* ── Search / Filter bar ───────────────────────────────── */}
          <div className="px-5 py-3 border-b border-gray-200 bg-gray-50 flex flex-wrap items-center gap-2" data-testid="commission-filter-bar">
            <div className="relative flex-1 min-w-[200px] max-w-[340px]">
              <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search username or email…"
                className="w-full h-9 pl-8 pr-8 border border-gray-200 rounded-sm text-sm bg-white focus:border-emerald-400 focus:outline-none"
                data-testid="commission-search-input"
              />
              {searchTerm && (
                <button
                  type="button"
                  onClick={() => setSearchTerm("")}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                  data-testid="commission-search-clear"
                >
                  <X size={14} />
                </button>
              )}
            </div>
            <div className="inline-flex border border-gray-200 rounded-sm overflow-hidden bg-white" role="tablist" data-testid="commission-status-filter">
              {[
                { v: "all",       label: "All",              count: rawPreviews.length },
                { v: "ready",     label: "Ready to Credit",  count: rawPreviews.filter((p) => !p.would_skip).length },
                { v: "already",   label: "Already Credited", count: rawPreviews.filter((p) => p.would_skip === "already_credited").length },
                { v: "no_volume", label: "No Volume",        count: rawPreviews.filter((p) => p.would_skip === "no_volume").length },
              ].map((f) => (
                <button
                  key={f.v}
                  onClick={() => setStatusFilter(f.v)}
                  className={`px-3 h-9 text-xs font-semibold border-r last:border-r-0 border-gray-200 transition ${
                    statusFilter === f.v
                      ? "bg-emerald-500 text-white"
                      : "text-gray-600 hover:bg-gray-100"
                  }`}
                  data-testid={`commission-status-${f.v}`}
                >
                  {f.label} <span className={`ml-1 inline-block min-w-[18px] text-center text-[10px] rounded-sm px-1 ${
                    statusFilter === f.v ? "bg-white/25" : "bg-gray-200 text-gray-700"
                  }`}>{f.count}</span>
                </button>
              ))}
            </div>
            {filtersActive && (
              <button
                onClick={clearFilters}
                className="text-xs font-semibold text-rose-600 hover:text-rose-800 inline-flex items-center gap-1 ml-auto px-2 h-9"
                data-testid="commission-filters-clear"
              >
                <X size={12} /> Clear filters
              </button>
            )}
            <div className={`text-xs text-gray-500 ${filtersActive ? "" : "ml-auto"}`}>
              {allPreviews.length === rawPreviews.length
                ? `${rawPreviews.length} agents`
                : <><b className="text-emerald-700">{allPreviews.length}</b> of {rawPreviews.length} agents</>
              }
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="commission-preview-table">
              <thead className="bg-gray-50 text-[10.5px] uppercase tracking-wider text-gray-600 font-semibold">
                <tr>
                  <SortHeader label="Agent"               sortKey="username"   align="left"  sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Deposits"            sortKey="deposit"    align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Withdrawals"         sortKey="withdraw"   align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label="Net Volume"          sortKey="turnover"   align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <SortHeader label={`Commission @ ${pct}%`} sortKey="commission" align="right" sortBy={sortBy} sortDir={sortDir} onClick={toggleSort} />
                  <th className="text-left px-3 py-2.5">Status</th>
                  <th className="text-right px-3 py-2.5">Action</th>
                </tr>
              </thead>
              <tbody>
                {allPreviews.length === 0 && (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-400 py-8">
                      {rawPreviews.length === 0
                        ? "No active agents found."
                        : filtersActive
                          ? <>No agents match current filters · <button onClick={clearFilters} className="text-emerald-700 underline">clear filters</button></>
                          : "No agents to display."
                      }
                    </td>
                  </tr>
                )}
                {pagePreviews.map((p) => (
                  <tr key={p.agent_id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`commission-row-${p.agent_id}`}>
                    <td className="px-4 py-3">
                      <div className="font-mono text-emerald-700 text-[13px]">{p.agent_username}</div>
                      <div className="text-[11px] text-gray-500 truncate max-w-[180px]" title={p.agent_email}>{p.agent_email}</div>
                    </td>
                    <td className="px-3 py-3 text-right tabular text-gray-700">{fmtBdt(p.deposit_total)}</td>
                    <td className="px-3 py-3 text-right tabular text-gray-700">{fmtBdt(p.withdraw_total)}</td>
                    <td className="px-3 py-3 text-right tabular font-semibold text-gray-800">{fmtBdt(p.turnover)}</td>
                    <td className="px-3 py-3 text-right tabular font-bold text-amber-700">{fmtBdt(p.commission)}</td>
                    <td className="px-3 py-3">
                      {p.would_skip === "already_credited" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-blue-100 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-sm">
                          <CheckCircle2 size={10} /> Already credited
                        </span>
                      )}
                      {p.would_skip === "no_volume" && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-600 border border-gray-200 px-2 py-0.5 rounded-sm">
                          No volume
                        </span>
                      )}
                      {!p.would_skip && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-700 border border-emerald-200 px-2 py-0.5 rounded-sm">
                          <Mail size={10} /> Ready to credit
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 text-right">
                      <button
                        onClick={() => runForAgent(p.agent_id, p.agent_username)}
                        disabled={!!perAgentBusy[p.agent_id] || running || p.commission < 1}
                        className="text-[11px] font-semibold text-emerald-700 hover:text-emerald-900 underline disabled:opacity-40 disabled:no-underline disabled:cursor-not-allowed"
                        data-testid={`commission-rerun-${p.agent_id}`}
                        title={p.commission < 1 ? "No volume — nothing to credit" : "Force re-run for this agent (bypasses idempotency lock)"}
                      >
                        {perAgentBusy[p.agent_id] ? "Working…" : (p.would_skip === "already_credited" ? "Force re-run" : "Run only this")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              {allPreviews.length > 0 && (
                <tfoot className="bg-emerald-50 border-t-2 border-emerald-200 text-sm font-bold">
                  <tr>
                    <td className="px-4 py-2.5 text-emerald-900">
                      {filtersActive
                        ? `Filtered totals (${allPreviews.length} of ${rawPreviews.length})`
                        : `Totals (all ${rawPreviews.length})`}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular text-emerald-900">{fmtBdt(allPreviews.reduce((s, p) => s + (p.deposit_total || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right tabular text-emerald-900">{fmtBdt(allPreviews.reduce((s, p) => s + (p.withdraw_total || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right tabular text-emerald-900">{fmtBdt(allPreviews.reduce((s, p) => s + (p.turnover || 0), 0))}</td>
                    <td className="px-3 py-2.5 text-right tabular text-amber-700">{fmtBdt(allPreviews.reduce((s, p) => s + (p.commission || 0), 0))}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
          {/* Pagination — 12 agents per page */}
          {allPreviews.length > PAGE_SIZE && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              pageStart={pageStart}
              pageEnd={Math.min(pageEnd, allPreviews.length)}
              totalItems={allPreviews.length}
              onChange={setCurrentPage}
            />
          )}
        </div>
      )}

      {/* ── Live run result ───────────────────────────────────────── */}
      {runResult && (
        <div className="bg-emerald-50 border-2 border-emerald-300 rounded-lg p-4 mb-6 flex items-start gap-3" data-testid="commission-run-result">
          <CheckCircle2 size={20} className="text-emerald-600 mt-1 flex-shrink-0" />
          <div className="text-sm">
            <div className="font-bold text-emerald-900">Run complete · Run ID {runResult.run_id?.slice(0, 8)}…</div>
            <div className="text-emerald-800 mt-1">
              {runResult.credited} agent(s) credited · {runResult.email_sent} email(s) sent ·{" "}
              {runResult.skipped_already_run} already-credited (skipped) ·{" "}
              {runResult.skipped_no_volume} no-volume (skipped) ·{" "}
              {runResult.email_failed} email failure(s).
            </div>
          </div>
        </div>
      )}

      {/* ── History ───────────────────────────────────────────────── */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-testid="commission-history-card">
        <div className="px-5 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <HistoryIcon size={16} className="text-gray-500" />
            <h3 className="font-bold text-sm">Past Runs</h3>
          </div>
          <button onClick={loadHistory} className="text-xs text-gray-500 hover:text-gray-800 inline-flex items-center gap-1" data-testid="commission-history-refresh">
            <RefreshCw size={12} className={historyLoading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="commission-history-table">
            <thead className="bg-gray-50 text-[10.5px] uppercase tracking-wider text-gray-600 font-semibold">
              <tr>
                <th className="text-left px-4 py-2.5">Run At</th>
                <th className="text-left px-3 py-2.5">Month</th>
                <th className="text-right px-3 py-2.5">Rate</th>
                <th className="text-right px-3 py-2.5">Agents</th>
                <th className="text-right px-3 py-2.5">Credited</th>
                <th className="text-right px-3 py-2.5">Emails</th>
                <th className="text-right px-3 py-2.5">Skipped (already)</th>
                <th className="text-right px-3 py-2.5">Skipped (no vol)</th>
                <th className="text-left px-3 py-2.5">Trigger</th>
              </tr>
            </thead>
            <tbody>
              {history.length === 0 && !historyLoading && (
                <tr><td colSpan={9} className="text-center text-gray-400 py-8">No past runs yet — first run will appear here.</td></tr>
              )}
              {historyLoading && (
                <tr><td colSpan={9} className="text-center text-gray-400 py-8">Loading…</td></tr>
              )}
              {history.map((r) => (
                <tr key={r.id} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-700">{fmtDate(r.started_at)}</td>
                  <td className="px-3 py-2.5 font-mono">{r.month}</td>
                  <td className="px-3 py-2.5 text-right tabular text-amber-700 font-semibold">{Number(r.commission_pct).toFixed(2)}%</td>
                  <td className="px-3 py-2.5 text-right tabular">{r.agents_total}</td>
                  <td className="px-3 py-2.5 text-right tabular font-bold text-emerald-700">{r.credited}</td>
                  <td className="px-3 py-2.5 text-right tabular text-blue-700">
                    <span className="inline-flex items-center gap-1">
                      {r.email_failed > 0 ? <MailX size={10} className="text-rose-500" /> : <Mail size={10} className="text-emerald-500" />}
                      {r.email_sent}{r.email_failed > 0 ? ` / ${r.email_failed} fail` : ""}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-right tabular text-gray-500">{r.skipped_already_run}</td>
                  <td className="px-3 py-2.5 text-right tabular text-gray-500">{r.skipped_no_volume}</td>
                  <td className="px-3 py-2.5 text-[11px]">
                    <span className={`inline-block px-2 py-0.5 rounded-sm border ${
                      String(r.triggered_by).startsWith("cron")
                        ? "bg-purple-50 text-purple-700 border-purple-200"
                        : "bg-blue-50 text-blue-700 border-blue-200"
                    }`}>
                      {String(r.triggered_by || "—").startsWith("admin:") ? `admin · ${String(r.triggered_by).slice(6)}` : (r.triggered_by || "—")}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function SummaryTile({ label, value, accent, testId }) {
  const accentCls = {
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-900",
    amber: "border-amber-200 bg-amber-50 text-amber-900",
    gray: "border-gray-200 bg-gray-50 text-gray-700",
  }[accent] || "border-gray-200 bg-white text-gray-800";
  return (
    <div className={`rounded-md border p-3 ${accentCls}`} data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-bold opacity-80">{label}</div>
      <div className="text-lg font-bold mt-0.5 tabular">{value}</div>
    </div>
  );
}

// AlertCircle import is referenced for parity with other admin pages even
// if not currently rendered — keeps the icon available without lint churn
// if future error banners are added.
void AlertCircle;

// ─────────────────────────────────────────────────────────────────
// Sortable column header — used in the preview table
// ─────────────────────────────────────────────────────────────────
function SortHeader({ label, sortKey, align, sortBy, sortDir, onClick }) {
  const active = sortBy === sortKey;
  const alignCls = align === "right" ? "text-right" : "text-left";
  const justifyCls = align === "right" ? "justify-end" : "justify-start";
  return (
    <th className={`${alignCls} px-3 py-2.5 select-none`}>
      <button
        type="button"
        onClick={() => onClick(sortKey)}
        className={`inline-flex items-center gap-1 ${justifyCls} group hover:text-emerald-700 transition ${
          active ? "text-emerald-700" : ""
        }`}
        data-testid={`commission-sort-${sortKey}`}
      >
        <span>{label}</span>
        {active
          ? (sortDir === "asc"
              ? <ChevronUp size={12} className="text-emerald-600" />
              : <ChevronDown size={12} className="text-emerald-600" />)
          : <ArrowUpDown size={11} className="text-gray-300 group-hover:text-gray-500" />
        }
      </button>
    </th>
  );
}

// ─────────────────────────────────────────────────────────────────
// Pagination — compact prev/next + numbered pages with smart ellipsis
// ─────────────────────────────────────────────────────────────────
function Pagination({ currentPage, totalPages, pageStart, pageEnd, totalItems, onChange }) {
  // Build a compact page list with ellipsis for long ranges
  const pages = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const set = new Set([1, totalPages, currentPage, currentPage - 1, currentPage + 1]);
    const sorted = [...set].filter((n) => n >= 1 && n <= totalPages).sort((a, b) => a - b);
    const withGaps = [];
    sorted.forEach((n, i) => {
      if (i > 0 && n - sorted[i - 1] > 1) withGaps.push("…");
      withGaps.push(n);
    });
    return withGaps;
  }, [currentPage, totalPages]);

  return (
    <div
      className="px-4 py-3 border-t border-gray-200 bg-gray-50 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs"
      data-testid="commission-preview-pagination"
    >
      <div className="text-gray-600">
        Showing <b className="text-gray-900">{pageStart + 1}–{pageEnd}</b> of{" "}
        <b className="text-gray-900">{totalItems}</b> agents · 12 per page
      </div>
      <div className="flex items-center gap-1">
        <button
          onClick={() => onChange(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className="h-7 px-2 border border-gray-200 rounded-sm hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 font-semibold"
          data-testid="commission-page-prev"
        >
          ← Prev
        </button>
        {pages.map((p, idx) =>
          p === "…" ? (
            <span key={`gap-${idx}`} className="px-2 text-gray-400 select-none">…</span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`h-7 min-w-[28px] px-2 border rounded-sm font-semibold tabular ${
                p === currentPage
                  ? "border-emerald-500 bg-emerald-500 text-white"
                  : "border-gray-200 bg-white text-gray-700 hover:border-emerald-300 hover:text-emerald-700"
              }`}
              data-testid={`commission-page-${p}`}
            >
              {p}
            </button>
          )
        )}
        <button
          onClick={() => onChange(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className="h-7 px-2 border border-gray-200 rounded-sm hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1 font-semibold"
          data-testid="commission-page-next"
        >
          Next →
        </button>
      </div>
    </div>
  );
}
